import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Banknote, Plus, X, Search, Printer, Trash2, Receipt, Package, Calendar, ChevronDown, ChevronRight, Download } from "lucide-react";

const PAIEMENTS = [
  { v: "especes", label: "Espèces" },
  { v: "cb", label: "Carte bancaire" },
  { v: "cheque", label: "Chèque" },
  { v: "virement", label: "Virement" },
];
const PM_LABEL = Object.fromEntries(PAIEMENTS.map((p) => [p.v, p.label]));
const todayStr = () => new Date().toISOString().slice(0, 10);

const EMPTY = { lignes: [], remise: 0, tva_rate: 20, mode_paiement: "especes", client_id: "", vehicle_id: "", notes: "" };

export default function Encaissement() {
  const [form, setForm] = useState(EMPTY);
  const [list, setList] = useState([]);
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [partQuery, setPartQuery] = useState("");
  const [partResults, setPartResults] = useState([]);
  const [partSearching, setPartSearching] = useState(false);
  const [free, setFree] = useState({ designation: "", prix: "", quantite: 1 });
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("jour");
  const [history, setHistory] = useState([]);
  const [openDay, setOpenDay] = useState(null);
  const [dayItems, setDayItems] = useState({});

  const loadHistory = async () => {
    try { const { data } = await api.get("/encaissements/history"); setHistory(data); }
    catch (e) { setHistory([]); }
  };

  const toggleDay = async (jour) => {
    if (openDay === jour) { setOpenDay(null); return; }
    setOpenDay(jour);
    if (!dayItems[jour]) {
      try { const { data } = await api.get("/encaissements", { params: { jour } }); setDayItems((d) => ({ ...d, [jour]: data })); }
      catch (e) { setDayItems((d) => ({ ...d, [jour]: [] })); }
    }
  };

  const exportDayCsv = async (jour) => {
    try {
      const res = await api.get(`/encaissements/history/${jour}/csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a"); a.href = url; a.download = `caisse-${jour}.csv`; a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Erreur lors de l'export CSV"); }
  };

  const load = async () => {
    const [e, c, v] = await Promise.all([
      api.get("/encaissements", { params: { jour: todayStr() } }),
      api.get("/clients"), api.get("/vehicles"),
    ]);
    setList(e.data); setClients(c.data); setVehicles(v.data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const ql = partQuery.trim();
    if (ql.length < 2) { setPartResults([]); setPartSearching(false); return; }
    setPartSearching(true);
    const t = setTimeout(async () => {
      try { const { data } = await api.get("/parts/search", { params: { q: ql } }); setPartResults(data); }
      catch (e) { setPartResults([]); }
      finally { setPartSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [partQuery]);

  const addPart = (r) => {
    setForm((f) => ({ ...f, lignes: [...f.lignes, { stock_id: r.stock_id || null, vhu_part_id: r.source === "vhu" ? (r.part_id || null) : null, source: r.source || "", reference: r.reference || "", designation: r.designation, quantite: 1, prix: r.prix || 0 }] }));
    setPartQuery(""); setPartResults([]);
  };
  const addFree = () => {
    if (!free.designation.trim()) { toast.error("Désignation requise"); return; }
    setForm((f) => ({ ...f, lignes: [...f.lignes, { stock_id: null, vhu_part_id: null, source: "", reference: "", designation: free.designation.trim(), quantite: Number(free.quantite) || 1, prix: Number(free.prix) || 0 }] }));
    setFree({ designation: "", prix: "", quantite: 1 });
  };
  const setLine = (i, k, v) => setForm((f) => { const l = [...f.lignes]; l[i] = { ...l[i], [k]: Number(v) }; return { ...f, lignes: l }; });
  const rmLine = (i) => setForm((f) => ({ ...f, lignes: f.lignes.filter((_, x) => x !== i) }));

  const totalTtc = Math.max(0, form.lignes.reduce((a, l) => a + l.quantite * l.prix, 0) - Number(form.remise || 0));
  const totalHt = form.tva_rate ? totalTtc / (1 + Number(form.tva_rate) / 100) : totalTtc;
  const totalTva = totalTtc - totalHt;

  const clientVehicles = form.client_id ? vehicles.filter((v) => v.client_id === form.client_id) : vehicles;

  useEffect(() => { if (tab === "archives" && history.length === 0) loadHistory(); }, [tab]);

  const fmtDay = (s) => {
    try { return new Date(s + "T12:00:00").toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" }); }
    catch { return s; }
  };

  const submit = async () => {
    if (form.lignes.length === 0) { toast.error("Ajoutez au moins une ligne"); return; }
    setSaving(true);
    try {
      const payload = { ...form, remise: Number(form.remise) || 0, tva_rate: Number(form.tva_rate) || 0 };
      const { data } = await api.post("/encaissements", payload);
      toast.success(`Encaissement ${data.numero} enregistré`);
      setForm(EMPTY);
      load(); loadHistory(); setDayItems({});
      openTicket(data.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const openTicket = async (id) => {
    try {
      const res = await api.get(`/encaissements/${id}/ticket`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
    } catch (e) { toast.error("Erreur lors de la génération du reçu"); }
  };

  const del = async (id) => {
    if (!window.confirm("Annuler cet encaissement ? Le stock vendu sera restitué.")) return;
    try { await api.delete(`/encaissements/${id}`); toast.success("Encaissement annulé"); load(); loadHistory(); setDayItems({}); }
    catch (e) { toast.error("Erreur lors de l'annulation"); }
  };

  const caisse = list.reduce((a, e) => a + (e.total_ttc || 0), 0);
  const parMode = PAIEMENTS.map((p) => ({ ...p, total: list.filter((e) => e.mode_paiement === p.v).reduce((a, e) => a + (e.total_ttc || 0), 0) }));

  return (
    <div className="space-y-5 animate-fade-up" data-testid="encaissement-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-[#F8FAFC] flex items-center gap-2"><Banknote className="w-7 h-7 text-emerald-400" />Encaissement rapide</h1>
        <p className="text-[#94A3B8] text-sm">Vente au comptoir. Client et véhicule facultatifs. La facturation reste gérée séparément.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Caisse form */}
        <div className="lg:col-span-2 bg-[#182232] border border-[#2A364F] rounded-xl p-5 space-y-4">
          <div>
            <Label className="text-[#94A3B8]">Rechercher un article (stock + VHU)</Label>
            <div className="relative mt-1">
              <Input data-testid="caisse-part-search" value={partQuery} onChange={(e) => setPartQuery(e.target.value)} placeholder="🔍 Référence ou désignation…" className="bg-[#0A0D14] border-[#2A364F]" />
              {partQuery.trim().length >= 2 && (
                <div data-testid="caisse-part-results" className="absolute z-20 mt-1 w-full max-h-72 overflow-auto rounded-lg border border-[#2A364F] bg-[#121824] shadow-xl">
                  {partSearching && <div className="px-3 py-2 text-xs text-[#64748B]">Recherche…</div>}
                  {!partSearching && partResults.length === 0 && <div className="px-3 py-2 text-xs text-[#64748B]">Aucun article trouvé</div>}
                  {partResults.map((r, i) => (
                    <button key={i} type="button" data-testid={`caisse-part-result-${i}`} onClick={() => addPart(r)} className="w-full text-left px-3 py-2 hover:bg-[#1E2A3E] border-b border-[#2A364F]/50 last:border-0 flex items-center gap-2">
                      <span className={`shrink-0 text-[9px] px-1.5 py-0.5 rounded border ${r.source === "vhu" ? "bg-blue-500/10 text-blue-400 border-blue-500/30" : "bg-emerald-500/10 text-emerald-400 border-emerald-500/30"}`}>{r.source === "vhu" ? "VHU" : "STOCK"}</span>
                      <span className="flex-1 min-w-0"><span className="block text-sm text-[#F8FAFC] truncate">{r.designation}</span><span className="block text-[11px] text-[#64748B] truncate">{r.reference && <span className="font-mono">{r.reference}</span>}{r.source === "vhu" ? (r.vhu_label ? ` · ${r.vhu_label}` : "") : ` · stock ${r.quantite}`}</span></span>
                      <span className="shrink-0 text-xs font-mono text-[#94A3B8]">{Number(r.prix || 0).toFixed(2)} €</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex gap-2 items-end">
            <div className="flex-1"><Label className="text-[#64748B] text-xs">Ligne libre</Label><Input data-testid="caisse-free-designation" value={free.designation} onChange={(e) => setFree({ ...free, designation: e.target.value })} placeholder="Désignation" className="bg-[#0A0D14] border-[#2A364F] mt-1" /></div>
            <div className="w-16"><Label className="text-[#64748B] text-xs">Qté</Label><Input data-testid="caisse-free-qty" type="number" value={free.quantite} onChange={(e) => setFree({ ...free, quantite: e.target.value })} className="bg-[#0A0D14] border-[#2A364F] mt-1" /></div>
            <div className="w-24"><Label className="text-[#64748B] text-xs">Prix TTC</Label><Input data-testid="caisse-free-price" type="number" step="0.01" value={free.prix} onChange={(e) => setFree({ ...free, prix: e.target.value })} className="bg-[#0A0D14] border-[#2A364F] mt-1" /></div>
            <Button type="button" data-testid="caisse-add-free" onClick={addFree} className="bg-[#3B82F6] hover:bg-[#2563EB] text-white"><Plus className="w-4 h-4" /></Button>
          </div>

          {/* Lines */}
          <div className="space-y-2">
            {form.lignes.length === 0 && <div className="text-center text-[#64748B] py-6 text-sm flex flex-col items-center gap-2"><Package className="w-6 h-6" />Aucun article. Recherchez ou ajoutez une ligne libre.</div>}
            {form.lignes.map((l, i) => (
              <div key={i} data-testid={`caisse-line-${i}`} className="flex items-center gap-2 bg-[#0A0D14] rounded-lg p-2 border border-[#2A364F]">
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-[#F8FAFC] truncate">{l.designation}{l.source === "vhu" && <span className="ml-2 text-[10px] text-blue-400">VHU</span>}{l.stock_id && <span className="ml-2 text-[10px] text-emerald-400">STOCK</span>}</div>
                  {l.reference && <div className="text-[11px] font-mono text-[#64748B]">{l.reference}</div>}
                </div>
                <Input type="number" value={l.quantite} onChange={(e) => setLine(i, "quantite", e.target.value)} className="bg-[#182232] border-[#2A364F] w-14" title="Qté" />
                <Input type="number" step="0.01" value={l.prix} onChange={(e) => setLine(i, "prix", e.target.value)} className="bg-[#182232] border-[#2A364F] w-24" title="Prix TTC" />
                <span className="w-24 text-right font-mono text-sm text-[#F59E0B]">{(l.quantite * l.prix).toFixed(2)} €</span>
                <button data-testid={`caisse-rm-line-${i}`} onClick={() => rmLine(i)} className="text-[#64748B] hover:text-[#EF4444]"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2 border-t border-[#2A364F]">
            <div><Label className="text-[#64748B] text-xs">Remise (€)</Label><Input data-testid="caisse-remise" type="number" step="0.01" value={form.remise} onChange={(e) => setForm({ ...form, remise: e.target.value })} className="bg-[#0A0D14] border-[#2A364F] mt-1" /></div>
            <div><Label className="text-[#64748B] text-xs">TVA (%)</Label><Input data-testid="caisse-tva" type="number" value={form.tva_rate} onChange={(e) => setForm({ ...form, tva_rate: e.target.value })} className="bg-[#0A0D14] border-[#2A364F] mt-1" /></div>
            <div>
              <Label className="text-[#64748B] text-xs">Paiement</Label>
              <Select value={form.mode_paiement} onValueChange={(v) => setForm({ ...form, mode_paiement: v })}>
                <SelectTrigger data-testid="caisse-paiement" className="mt-1 bg-[#0A0D14] border-[#2A364F]"><SelectValue /></SelectTrigger>
                <SelectContent>{PAIEMENTS.map((p) => <SelectItem key={p.v} value={p.v}>{p.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-end"><div className="text-right w-full"><div className="text-[10px] text-[#64748B]">Total TTC</div><div data-testid="caisse-total" className="font-mono font-extrabold text-xl text-emerald-400">{totalTtc.toFixed(2)} €</div></div></div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[#64748B] text-xs">Client (facultatif)</Label>
              <Select value={form.client_id || "none"} onValueChange={(v) => setForm({ ...form, client_id: v === "none" ? "" : v, vehicle_id: "" })}>
                <SelectTrigger data-testid="caisse-client" className="mt-1 bg-[#0A0D14] border-[#2A364F]"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">— Aucun —</SelectItem>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.nom}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#64748B] text-xs">Véhicule (facultatif)</Label>
              <Select value={form.vehicle_id || "none"} onValueChange={(v) => setForm({ ...form, vehicle_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="caisse-vehicle" className="mt-1 bg-[#0A0D14] border-[#2A364F]"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">— Aucun —</SelectItem>{clientVehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.marque} {v.modele} — {v.immatriculation}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex justify-between items-center text-xs text-[#64748B]">
            <span>HT {totalHt.toFixed(2)} € · TVA {totalTva.toFixed(2)} €</span>
            <Button data-testid="caisse-submit" onClick={submit} disabled={saving || form.lignes.length === 0} className="bg-emerald-500 hover:bg-emerald-600 text-[#0A0D14] font-bold h-11 px-6"><Receipt className="w-4 h-4 mr-2" />Encaisser {totalTtc.toFixed(2)} €</Button>
          </div>
        </div>

        {/* Right panel: Jour / Archives */}
        <div className="space-y-3">
          <div className="flex gap-1 bg-[#0A0D14] border border-[#2A364F] rounded-lg p-1">
            <button data-testid="caisse-tab-jour" onClick={() => setTab("jour")} className={`flex-1 text-sm font-semibold rounded-md py-1.5 transition-colors ${tab === "jour" ? "bg-emerald-500 text-[#0A0D14]" : "text-[#94A3B8] hover:text-[#F8FAFC]"}`}>Caisse du jour</button>
            <button data-testid="caisse-tab-archives" onClick={() => setTab("archives")} className={`flex-1 text-sm font-semibold rounded-md py-1.5 transition-colors ${tab === "archives" ? "bg-emerald-500 text-[#0A0D14]" : "text-[#94A3B8] hover:text-[#F8FAFC]"}`}>Archives</button>
          </div>

          {tab === "jour" && (<>
          <div className="bg-gradient-to-br from-emerald-500/20 to-[#182232] border border-emerald-500/30 rounded-xl p-5">
            <div className="text-[#94A3B8] text-xs uppercase tracking-wider">Caisse du jour</div>
            <div data-testid="caisse-jour-total" className="font-mono font-extrabold text-3xl text-emerald-400 mt-1">{caisse.toFixed(2)} €</div>
            <div className="text-xs text-[#64748B]">{list.length} encaissement{list.length > 1 ? "s" : ""}</div>
            <div className="mt-3 space-y-1">
              {parMode.map((p) => (
                <div key={p.v} className="flex justify-between text-xs"><span className="text-[#94A3B8]">{p.label}</span><span className="font-mono text-[#CBD5E1]">{p.total.toFixed(2)} €</span></div>
              ))}
            </div>
          </div>

          <div className="bg-[#182232] border border-[#2A364F] rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[#94A3B8] text-xs uppercase tracking-wider">Aujourd'hui</div>
              {list.length > 0 && <button data-testid="caisse-jour-csv" onClick={() => exportDayCsv(todayStr())} className="text-[11px] text-[#3B82F6] hover:text-[#60A5FA] flex items-center gap-1"><Download className="w-3.5 h-3.5" />CSV</button>}
            </div>
            <div className="space-y-2 max-h-[420px] overflow-auto">
              {list.length === 0 && <div className="text-xs text-[#64748B] py-4 text-center">Aucun encaissement aujourd'hui.</div>}
              {list.map((e) => (
                <div key={e.id} data-testid={`caisse-item-${e.id}`} className="flex items-center justify-between gap-2 bg-[#0A0D14] rounded-lg p-2.5 border border-[#2A364F]">
                  <div className="min-w-0">
                    <div className="text-sm text-[#F8FAFC] font-mono">{e.numero}</div>
                    <div className="text-[11px] text-[#64748B]">{(e.created_at || "").slice(11, 16)} · {PM_LABEL[e.mode_paiement] || e.mode_paiement}{e.client ? ` · ${e.client.nom}` : ""}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-mono font-bold text-emerald-400">{Number(e.total_ttc).toFixed(2)} €</div>
                    <div className="flex gap-1 justify-end mt-0.5">
                      <button data-testid={`caisse-ticket-${e.id}`} onClick={() => openTicket(e.id)} className="text-[#64748B] hover:text-[#3B82F6]" title="Reçu"><Printer className="w-4 h-4" /></button>
                      <button data-testid={`caisse-del-${e.id}`} onClick={() => del(e.id)} className="text-[#64748B] hover:text-[#EF4444]" title="Annuler"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          </>)}

          {tab === "archives" && (
            <div className="bg-[#182232] border border-[#2A364F] rounded-xl p-4" data-testid="caisse-archives">
              <div className="text-[#94A3B8] text-xs uppercase tracking-wider mb-3 flex items-center gap-2"><Calendar className="w-4 h-4" />Suivi des ventes — jour par jour</div>
              <div className="space-y-2 max-h-[560px] overflow-auto">
                {history.length === 0 && <div className="text-xs text-[#64748B] py-6 text-center">Aucune vente archivée.</div>}
                {history.map((h) => (
                  <div key={h.jour} data-testid={`caisse-archive-${h.jour}`} className="bg-[#0A0D14] rounded-lg border border-[#2A364F] overflow-hidden">
                    <button onClick={() => toggleDay(h.jour)} data-testid={`caisse-archive-toggle-${h.jour}`} className="w-full flex items-center justify-between gap-2 p-3 hover:bg-[#121824] transition-colors">
                      <div className="flex items-center gap-2 min-w-0">
                        {openDay === h.jour ? <ChevronDown className="w-4 h-4 text-[#64748B] shrink-0" /> : <ChevronRight className="w-4 h-4 text-[#64748B] shrink-0" />}
                        <div className="min-w-0 text-left">
                          <div className="text-sm text-[#F8FAFC] capitalize truncate">{fmtDay(h.jour)}</div>
                          <div className="text-[11px] text-[#64748B]">{h.count} vente{h.count > 1 ? "s" : ""} · HT {h.total_ht.toFixed(2)} € · TVA {h.total_tva.toFixed(2)} €</div>
                        </div>
                      </div>
                      <div className="font-mono font-extrabold text-emerald-400 shrink-0">{h.total_ttc.toFixed(2)} €</div>
                    </button>
                    {openDay === h.jour && (
                      <div className="border-t border-[#2A364F] p-3 space-y-2 bg-[#0A0D14]">
                        <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                          {PAIEMENTS.map((p) => (
                            <div key={p.v} className="flex justify-between text-[11px]"><span className="text-[#94A3B8]">{p.label}</span><span className="font-mono text-[#CBD5E1]">{(h.par_mode?.[p.v] || 0).toFixed(2)} €</span></div>
                          ))}
                        </div>
                        <button data-testid={`caisse-archive-csv-${h.jour}`} onClick={() => exportDayCsv(h.jour)} className="text-[11px] text-[#3B82F6] hover:text-[#60A5FA] flex items-center gap-1"><Download className="w-3.5 h-3.5" />Exporter CSV</button>
                        <div className="space-y-1.5 pt-1 border-t border-[#2A364F]/60">
                          {(dayItems[h.jour] || []).map((e) => (
                            <div key={e.id} className="flex items-center justify-between gap-2">
                              <div className="min-w-0">
                                <span className="text-xs font-mono text-[#F8FAFC]">{e.numero}</span>
                                <span className="text-[10px] text-[#64748B] ml-1">{(e.created_at || "").slice(11, 16)} · {PM_LABEL[e.mode_paiement] || e.mode_paiement}</span>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <span className="font-mono text-xs text-emerald-400">{Number(e.total_ttc).toFixed(2)} €</span>
                                <button data-testid={`caisse-archive-ticket-${e.id}`} onClick={() => openTicket(e.id)} className="text-[#64748B] hover:text-[#3B82F6]" title="Reçu"><Printer className="w-3.5 h-3.5" /></button>
                              </div>
                            </div>
                          ))}
                          {!dayItems[h.jour] && <div className="text-[11px] text-[#64748B] text-center py-1">Chargement…</div>}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
